import { monthName, weekdayName, weekdayOf } from '@app/shared'
import type { JournalHeatDay } from '../../lib/data'

import { HEAT_FILL, heatLevel } from './util'

// ============================================================================
// THE WRITING HEATMAP — docs/design/handoff/"LifeOS Journal.dc.html", option 5a.
//
// 26 columns of seven days, 10px cells, 3px gaps, radius 2, month labels beneath.
// Columns are Monday-started weeks; the newest column is the current week and is
// SHORT, because the rest of this week has not happened. The mockup draws a full
// rectangle (its data is random), but rendering four grey squares for Thursday
// through Sunday would say "no entry" about days that do not exist yet — which is
// the one thing this screen is built not to do.
// ============================================================================

const CELL = 10
const GAP = 3
const STEP = CELL + GAP

export function Heatmap({ days }: { days: JournalHeatDay[] }) {
  // Weeks, in order. The API hands back a run starting on a Monday, so a new column
  // opens on every Monday rather than every seventh element — which stays correct
  // even if the window ever starts mid-week.
  const weeks: JournalHeatDay[][] = []
  for (const day of days) {
    if (weeks.length === 0 || weekdayOf(day.date) === 1) weeks.push([])
    weeks[weeks.length - 1]!.push(day)
  }

  // One label per run of columns that begin in the same month.
  const labels: Array<{ month: string; columns: number }> = []
  for (const week of weeks) {
    const label = monthName(week[0]!.date, true)
    const last = labels[labels.length - 1]
    if (last && last.month === label) last.columns += 1
    else labels.push({ month: label, columns: 1 })
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-[7px]">
      <div className="flex items-center gap-[10px]">
        <h2 className="text-[10px] font-semibold uppercase leading-none tracking-[0.08em] text-faint">
          Writing days · last {weeks.length} weeks
        </h2>
        <div className="ml-auto flex items-center gap-[5px]" aria-hidden>
          <span className="text-[10px] leading-none text-faintest">less</span>
          {[0, 1, 2, 3].map((level) => (
            <span
              key={level}
              className={`size-[9px] rounded-[2px] ${HEAT_FILL[level === 3 ? 4 : level]}`}
            />
          ))}
          <span className="text-[10px] leading-none text-faintest">more</span>
        </div>
      </div>

      <div className="flex" style={{ gap: `${GAP}px` }}>
        {weeks.map((week) => (
          <div key={week[0]!.date} className="flex flex-col" style={{ gap: `${GAP}px` }}>
            {week.map((day) => (
              <Cell key={day.date} day={day} />
            ))}
          </div>
        ))}
      </div>

      <div className="flex text-[9.5px] font-medium leading-none text-faintest" style={{ gap: `${GAP}px` }}>
        {labels.map((label, i) => (
          <span key={`${label.month}-${i}`} style={{ width: `${label.columns * STEP - GAP}px` }}>
            {label.month}
          </span>
        ))}
      </div>
    </div>
  )
}

/**
 * One day. The `title` is the only place the count is spelled out, and it says "no entry" rather
 * than "0 words" — a day nobody wrote on did not write zero words, it was not written on.
 */
function Cell({ day }: { day: JournalHeatDay }) {
  const level = heatLevel(day.words)
  const when = `${weekdayName(day.date, true)} ${day.date}`
  return (
    <span
      title={day.words ? `${when} · ${day.words} words` : `${when} · no entry`}
      className={`rounded-[2px] ${HEAT_FILL[level]}`}
      style={{ width: `${CELL}px`, height: `${CELL}px` }}
    />
  )
}

/**
 * The skeleton, at the final geometry: 26 columns of seven cells, no colour.
 *
 * The `sr-only` line is not decoration. It is the only thing on a skeleton a screen reader can
 * announce, and it doubles as this repo's settled-signal: app.smoke.test.tsx flushes until no
 * ellipsis is left on screen, so a pending surface with no "Loading…" reports itself as finished
 * and the test reads a half-rendered page as the final one.
 */
export function HeatmapSkeleton({ weeks = 26 }: { weeks?: number }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-[7px]">
      <span className="sr-only">Loading…</span>
      <div className="h-[10px] w-[190px] rounded-full bg-fill" aria-hidden />
      <div className="flex" style={{ gap: `${GAP}px` }}>
        {Array.from({ length: weeks }, (_, w) => (
          <div key={w} className="flex flex-col" style={{ gap: `${GAP}px` }}>
            {Array.from({ length: 7 }, (_, d) => (
              <span
                key={d}
                className="rounded-[2px] bg-heat-0"
                style={{ width: `${CELL}px`, height: `${CELL}px` }}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="h-[9px] w-[300px] rounded-full bg-line-3" />
    </div>
  )
}
